import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AlternativeItineraryDoubleCardLayerMobileContentProps } from './types';
import { useAlternativeItineraryActions } from 'clientState/providers/AlternativeItineraryProvider/AlternativeItineraryCalculatorProvider';
import {
  Box,
  DrawerBottomSheetContent,
} from '@edreamsodigeo/prisma-design-system';
import AlternativeItineraryDoubleCardLayerTopSection from './AlternativeItineraryDoubleCardLayerTopSection';
import AlternativeItineraryDoubleCardDropdownLayerTopSection from './AlternativeItineraryDoubleCardDropdownLayerTopSection';
import AlternativeItineraryDoubleCardLayerBottomSection from './AlternativeItineraryDoubleCardLayerBottomSection';
import useDeviceHeight from 'clientState/hooks/useDeviceHeight';

export const AlternativeItineraryDoubleCardDropdownLayerMobileContent: FC<
  AlternativeItineraryDoubleCardLayerMobileContentProps
> = ({
  itinerary,
  valueProposition,
  valuePropositions,
  additionalInformation,
  discountAmount,
  nonIssuable = true,
  isUnavailable = false,
  showContinueButton,
  selectedItinerary,
  backgroundColor,
  onContinue,
  onChange,
  overrideAction,
  trackingHandler,
  simpleUI,
}) => {
  const [isTopSectionExpanded, setIsTopSectionExpanded] = useState(false);
  const [isDelayingAlternative, setIsDelayingAlternative] = useState(true);
  const [altContentHeight, setAltContentHeight] = useState(0);
  const [selectedContentHeight, setSelectedContentHeight] = useState(0);
  const [isSmallMobileDevice, setIsSmallMobileDevice] = useState(false);
  const [hasLargeAlternativeCard, setHasLargeAlternativeCard] = useState(false);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(0);
  const [dropdownMinHeight, setDropdownMinHeight] = useState(0);
  const [hasToOpenBottom, setHasToOpenBottom] = useState(false);
  const [bottomValue, setBottomValue] = useState(0);

  const selectedContentRef = useRef<HTMLDivElement>(null);
  const altContentRef = useRef<HTMLDivElement>(null);

  const deviceHeight = useDeviceHeight();

  const legs = itinerary?.legs || [];
  const isRoundTrip = legs.length > 1;
  const thresholdOpenHeight = isRoundTrip ? 120 : 130;
  const thresholdCloseHeight = isRoundTrip ? 120 : 54;
  const smallDeviceHeight = 667;

  const maxAlternativeCardHeightLarge = 282;
  const maxAlternativeCardHeightSmall = 140;

  const handleTitleClick = () => {
    setIsTopSectionExpanded(!isTopSectionExpanded);
  };
  const { isItineraryUnavailable } = useAlternativeItineraryActions();
  const isSelectedItineraryUnavailable =
    isItineraryUnavailable(selectedItinerary) || isUnavailable;

  useLayoutEffect(() => {
    if (altContentRef.current) {
      setAltContentHeight(altContentRef.current.clientHeight);
    }
    if (selectedContentRef.current) {
      setSelectedContentHeight(selectedContentRef.current.clientHeight);
    }

    if (isRoundTrip) {
      if (deviceHeight <= smallDeviceHeight) {
        setIsSmallMobileDevice(true);
        setHasToOpenBottom(true);
      }

      const heightDiff = deviceHeight - altContentHeight;
      if (
        heightDiff <=
        (isSmallMobileDevice
          ? maxAlternativeCardHeightSmall
          : maxAlternativeCardHeightLarge)
      ) {
        setHasLargeAlternativeCard(true);
        setHasToOpenBottom(true);
      }
    }

    setDropdownMinHeight(
      altContentHeight + selectedContentHeight - thresholdCloseHeight
    );
    setDropdownMaxHeight(
      altContentHeight + selectedContentHeight + thresholdOpenHeight
    );
  }, [
    altContentHeight,
    deviceHeight,
    dropdownMaxHeight,
    isRoundTrip,
    isSmallMobileDevice,
    selectedContentHeight,
    thresholdCloseHeight,
    thresholdOpenHeight,
  ]);

  useEffect(() => {
    setTimeout(() => setIsDelayingAlternative(false), 300);
  }, [setIsDelayingAlternative]);

  useEffect(() => {
    const calculateBottomValue = (
      isTopSectionExpanded: boolean,
      hasLargeAlternativeCard: boolean,
      isSmallMobileDevice: boolean
    ): number => {
      const thresholdBottomValues = {
        expanded: {
          large: { small: -210, large: -140 },
          small: { small: -160, large: 0 },
        },
        collapsed: {
          large: { small: -76, large: 2 },
          small: { small: -15, large: 0 },
        },
      };

      const sectionState = isTopSectionExpanded ? 'expanded' : 'collapsed';
      const cardSize = hasLargeAlternativeCard ? 'large' : 'small';
      const deviceSize = isSmallMobileDevice ? 'small' : 'large';

      return thresholdBottomValues[sectionState][cardSize][deviceSize];
    };

    const value = calculateBottomValue(
      isTopSectionExpanded,
      hasLargeAlternativeCard,
      isSmallMobileDevice
    );

    setBottomValue(value);
  }, [
    altContentHeight,
    hasLargeAlternativeCard,
    isSmallMobileDevice,
    isTopSectionExpanded,
    selectedContentHeight,
  ]);

  return (
    <DrawerBottomSheetContent
      scroll={hasToOpenBottom && isTopSectionExpanded}
      sx={{
        padding: '0',
        backgroundColor: backgroundColor,
        position: 'relative',
        maxHeight: '100%',
        overflowY: isTopSectionExpanded ? 'scroll' : 'hidden !important',
      }}
    >
      <Box
        sx={{
          overflow: 'hidden',
          position: 'relative',
          height: `${24 + altContentHeight + selectedContentHeight}px`,
          maxHeight:
            isTopSectionExpanded && !hasToOpenBottom
              ? `${dropdownMaxHeight}px`
              : `${dropdownMinHeight}px`,
          zIndex: 1,
          transition: 'max-height 0.6s ease-in-out',
        }}
      >
        <div ref={selectedContentRef}>
          <AlternativeItineraryDoubleCardDropdownLayerTopSection
            itinerary={selectedItinerary}
            nonIssuable={nonIssuable}
            isUnavailable={isSelectedItineraryUnavailable}
            isTopSectionExpanded={isTopSectionExpanded}
            onTitleClick={handleTitleClick}
            trackingHandler={trackingHandler}
            onSelectOriginalItinerary={onContinue}
            overrideAction={overrideAction}
          />
        </div>
      </Box>
      <Box
        borderTopLeftRadius="big"
        borderTopRightRadius="big"
        backgroundColor="neutral.0"
        sx={{
          position: 'absolute',
          bottom: `${bottomValue}px`,
          overflow: 'hidden',
          width: '100%',
          zIndex: 2,
          height: isDelayingAlternative ? '0' : `${altContentHeight}px`,
          transition: 'bottom 0.6s ease-in-out, height 0.6s linear',
          boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.3)',
        }}
      >
        <div ref={altContentRef}>
          <AlternativeItineraryDoubleCardLayerBottomSection
            itinerary={itinerary}
            selectedItinerary={selectedItinerary}
            nonIssuable={nonIssuable}
            showContinueButton={showContinueButton}
            paddingTop={'big'}
            valueProposition={valueProposition}
            valuePropositions={valuePropositions}
            additionalInformation={additionalInformation}
            discountAmount={discountAmount}
            onContinue={onContinue}
            onChange={onChange}
            overrideAction={overrideAction}
            trackingHandler={trackingHandler}
            simpleUI={simpleUI}
          />
        </div>
      </Box>
    </DrawerBottomSheetContent>
  );
};

const AlternativeItineraryDoubleCardLayerMobileContent: FC<
  AlternativeItineraryDoubleCardLayerMobileContentProps
> = ({
  itinerary,
  valueProposition,
  valuePropositions,
  additionalInformation,
  discountAmount,
  isBrokenFlow,
  nonIssuable = true,
  isUnavailable = false,
  showContinueButton,
  selectedItinerary,
  backgroundColor,
  onContinue,
  onChange,
  trackingHandler,
  simpleUI,
}) => {
  const { isItineraryUnavailable } = useAlternativeItineraryActions();
  const isSelectedItineraryUnavailable =
    isItineraryUnavailable(selectedItinerary) || isUnavailable;

  return (
    <DrawerBottomSheetContent
      scroll={true}
      sx={{
        padding: '0',
        backgroundColor: backgroundColor,
      }}
    >
      <AlternativeItineraryDoubleCardLayerTopSection
        itinerary={selectedItinerary}
        nonIssuable={nonIssuable}
        isBrokenFlow={isBrokenFlow}
        isUnavailable={isSelectedItineraryUnavailable}
        trackingHandler={trackingHandler}
      />
      <AlternativeItineraryDoubleCardLayerBottomSection
        itinerary={itinerary}
        selectedItinerary={selectedItinerary}
        nonIssuable={nonIssuable}
        showContinueButton={showContinueButton}
        valueProposition={valueProposition}
        valuePropositions={valuePropositions}
        additionalInformation={additionalInformation}
        discountAmount={discountAmount}
        onContinue={onContinue}
        onChange={onChange}
        trackingHandler={trackingHandler}
        simpleUI={simpleUI}
      />
    </DrawerBottomSheetContent>
  );
};

export default AlternativeItineraryDoubleCardLayerMobileContent;
